import { useState, useRef, useEffect } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";
import { LogOut, ChevronDown, KeyRound, User, FlaskConical } from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClickAway = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [menuOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate("/login", { replace: true });
  };

  if (!user) return null;

  const isAdmin = user.role === "admin";
  const initials = user.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      <header
        className="w-full bg-white border-b border-slate-200 sticky top-0 z-30"
        data-testid="app-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <img
                src={BRAND.logoUrl}
                alt="Wellform MD"
                className="h-10 w-10 object-contain"
                draggable="false"
              />
              <div className="leading-tight">
                <div className="text-sm font-bold" style={{ color: BRAND.teal }}>
                  {BRAND.name}
                </div>
                <div
                  className="text-[10px] tracking-widest uppercase"
                  style={{ color: BRAND.purple }}
                >
                  Timesheet
                </div>
              </div>
            </div>

            {isAdmin && (
              <nav className="hidden sm:flex items-center gap-1" data-testid="admin-nav">
                <NavItem to="/admin" label="Dashboard" testId="nav-dashboard" />
                <NavItem to="/admin/team" label="Team" testId="nav-team" />
                <NavItem to="/admin/projects" label="Projects" testId="nav-projects" />
                <NavItem to="/admin/settings" label="Settings" testId="nav-settings" />
              </nav>
            )}
          </div>

          {/* User menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="user-menu-button"
              className="flex items-center gap-2.5 px-2 py-1 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${BRAND.teal}, ${BRAND.purple})`,
                }}
                data-testid="user-avatar"
              >
                {initials}
              </div>
              <div className="hidden sm:flex flex-col items-start leading-tight">
                <span
                  className="text-sm font-medium text-slate-800"
                  data-testid="header-user-name"
                >
                  {user.name}
                </span>
                <span
                  className="text-[10px] uppercase tracking-wider font-semibold"
                  style={{ color: isAdmin ? BRAND.purple : BRAND.teal }}
                  data-testid="header-user-role"
                >
                  {user.role}
                </span>
              </div>
              <ChevronDown size={14} className="text-slate-400" />
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-40"
                data-testid="user-menu"
              >
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <User size={14} style={{ color: BRAND.teal }} />
                    {user.name}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 truncate">{user.email}</div>
                </div>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setPwdOpen(true);
                  }}
                  data-testid="menu-change-password"
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                >
                  <KeyRound size={14} className="text-slate-500" />
                  Change password
                </button>
                {isAdmin && (
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/dashboard?preview=1");
                    }}
                    data-testid="menu-preview-freelancer"
                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 border-t border-slate-100"
                  >
                    <FlaskConical size={14} style={{ color: BRAND.teal }} />
                    Preview freelancer view
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-slate-100"
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>

        {isAdmin && (
          <nav className="sm:hidden border-t border-slate-100 px-4 py-2 flex gap-1 overflow-x-auto">
            <NavItem to="/admin" label="Dashboard" testId="nav-dashboard-mobile" />
            <NavItem to="/admin/team" label="Team" testId="nav-team-mobile" />
            <NavItem to="/admin/projects" label="Projects" testId="nav-projects-mobile" />
            <NavItem to="/admin/settings" label="Settings" testId="nav-settings-mobile" />
          </nav>
        )}
      </header>

      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </>
  );
}

function NavItem({ to, label, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) =>
        `px-3 py-1.5 text-sm font-medium rounded-md transition ${
          isActive
            ? "bg-slate-100 text-slate-900"
            : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
        }`
      }
      style={({ isActive }) =>
        isActive ? { color: BRAND.teal, background: `${BRAND.teal}12` } : undefined
      }
    >
      {label}
    </NavLink>
  );
}
